import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Avatar,
  Paper,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import FlagIcon from "@mui/icons-material/Flag";

export default function AboutPage() {
  const milestones = [
    {
      year: "2024",
      title: "VeraInsight Founded",
      description:
        "VeraInsight Healthcare Inc founded with a mission to transform rare disease care through technology.",
    },
    {
      year: "2025",
      title: "AmyloidConnect Launched",
      description:
        "Launched AmyloidConnect.com, a comprehensive clinical trials platform for the amyloidosis community.",
    },
    {
      year: "2025",
      title: "150+ Trials Indexed",
      description:
        "Successfully indexed 150+ amyloidosis-specific clinical trials from ClinicalTrials.gov.",
    },
    {
      year: "2026",
      title: "NephroTrax Launched",
      description:
        "Expanded into kidney disease with NephroTrax — a connected chronic kidney disease platform for patients and providers.",
    },
    {
      year: "2026",
      title: "Compass Launched",
      description:
        "Launched Compass, a pediatric cancer resource navigator built for families — clinical trials and community support, searched together.",
    },
    {
      year: "In Progress",
      title: "ResourceClan",
      description:
        "Building ResourceClan, a general-purpose social-services resource directory, on the same platform foundation as our other products.",
    },
  ];

  return (
    <Box>
      {/* HERO */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a237e 0%, #00897b 100%)",
          color: "white",
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Chip
            label="About VeraInsight HealthCare"
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              color: "white",
              fontWeight: 600,
              mb: 3,
            }}
          />
          <Typography variant="h2" fontWeight={800} gutterBottom>
            Our Story
          </Typography>
          <Typography
            variant="h5"
            sx={{ opacity: 0.9, lineHeight: 1.7, fontWeight: 400 }}
          >
            We believe every patient deserves clear, accessible information
            about their healthcare options — regardless of how rare or complex
            their condition.
          </Typography>
        </Container>
      </Box>

      {/* MISSION & VISION */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#f8fafc" }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {[
              {
                icon: <FlagIcon sx={{ fontSize: 50 }} />,
                title: "Our Mission",
                color: "#1a237e",
                text: "To build disease-specific digital health platforms that connect patients, providers, and researchers — making complex healthcare journeys simpler, clearer, and more accessible for all.",
              },
              {
                icon: <VisibilityIcon sx={{ fontSize: 50 }} />,
                title: "Our Vision",
                color: "#00897b",
                text: "A world where every patient, regardless of their diagnosis, has clear access to clinical trials, educational resources, and the tools they need to navigate their healthcare journey.",
              },
              {
                icon: <RocketLaunchIcon sx={{ fontSize: 50 }} />,
                title: "Our Approach",
                color: "#f9a825",
                text: "We build focused, disease-specific platforms rather than generic tools. Each product is deeply tailored to the unique needs of its patient community, providers, and researchers.",
              },
            ].map((item, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    textAlign: "center",
                    p: 2,
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                  }}
                >
                  <CardContent>
                    <Box sx={{ color: item.color, mb: 2 }}>{item.icon}</Box>
                    <Typography variant="h5" fontWeight={700} gutterBottom>
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ lineHeight: 1.8 }}
                    >
                      {item.text}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* OUR TEAM — founder and advisors together as one group, compact
          cards side by side (same density as the stat cards on the
          Homepage), rather than a large standalone founder card followed
          by a separate advisors grid. */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "white" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography variant="h3" fontWeight={700} gutterBottom>
              Our Team
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{ maxWidth: 640, mx: "auto", fontWeight: 400 }}
            >
              One founder, clinical expertise, and community relationships
              that shape how our platforms actually serve patients.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            {[
              {
                logo: true,
                initial: "S",
                name: "Sandeep Sinha",
                role: "Founder",
                color: "#1a237e",
                detail: "Healthcare Tech · AWS",
              },
              {
                initial: "N",
                name: "Nitasha Sarswat, MD",
                role: "Chief Medical Advisor",
                color: "#00897b",
                detail: "University of Chicago Medicine",
              },
              {
                initial: "M",
                name: "Mike Lane",
                role: "Business Development Advisor",
                color: "#f9a825",
                detail: "Founder, Amyloidosis Army",
              },
              {
                initial: "H",
                name: "Hamza Naveed, MD",
                role: "Medical Advisor",
                color: "#764ba2",
                detail: "",
              },
              {
                initial: "A",
                name: "Art Still",
                role: "Business Development Advisor",
                color: "#d84315",
                detail: "Still4Life",
              },
            ].map((member, index) => (
              <Grid item xs={12} sm={6} md={2.4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    textAlign: "center",
                    p: 2,
                    transition: "all 0.3s ease",
                    "&:hover": { transform: "translateY(-4px)", boxShadow: 4 },
                  }}
                >
                  <CardContent sx={{ p: "8px !important" }}>
                    {member.logo ? (
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          mx: "auto",
                          mb: 1.5,
                          borderRadius: "50%",
                          overflow: "hidden",
                          background:
                            "linear-gradient(135deg, #1a237e 0%, #00897b 100%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          p: 1,
                        }}
                      >
                        <Box
                          component="img"
                          src="/logo.png"
                          alt="VeraInsight"
                          sx={{ width: "100%", height: "100%", objectFit: "contain" }}
                        />
                      </Box>
                    ) : (
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          mx: "auto",
                          mb: 1.5,
                          bgcolor: member.color,
                          fontSize: "1.3rem",
                          fontWeight: 700,
                        }}
                      >
                        {member.initial}
                      </Avatar>
                    )}
                    <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.3 }}>
                      {member.name}
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ color: member.color, fontWeight: 700, mb: 0.5 }}
                    >
                      {member.role}
                    </Typography>
                    {member.detail && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {member.detail}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* MILESTONES */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#f8fafc" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography variant="h3" fontWeight={700} gutterBottom>
              Our Journey
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {milestones.map((milestone, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper
                  sx={{
                    p: 3,
                    display: "flex",
                    gap: 2,
                    alignItems: "flex-start",
                    transition: "all 0.3s ease",
                    "&:hover": { boxShadow: 4 },
                  }}
                >
                  <Box
                    sx={{
                      minWidth: 60,
                      height: 60,
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg, #1a237e 0%, #00897b 100%)",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 800,
                      fontSize: "0.85rem",
                    }}
                  >
                    {milestone.year}
                  </Box>
                  <Box>
                    <Typography variant="h6" fontWeight={700} gutterBottom>
                      {milestone.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.7 }}
                    >
                      {milestone.description}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}